import { useState, useEffect } from 'react';

// API backends - selectable via ?api= query parameter
// Production uses CDN (api.pipeup.live) for better performance
const API_BACKENDS = {
  prod: 'https://api.pipeup.live',
  app: 'https://app.pipeup.live', // Direct to Meteor (bypass CDN)
  staging: 'https://staging.pipeup2.live.eu.meteorapp.com',
  local: 'http://localhost:4000',
};

/**
 * Get API base URL from query parameter or default
 * Usage: ?api=staging, ?api=local, or omit for production
 */
const getApiBaseUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const apiParam = params.get('api');

  if (apiParam && API_BACKENDS[apiParam]) {
    return API_BACKENDS[apiParam];
  }

  // Default to prod, or use env var for local dev
  return import.meta.env.VITE_API_BASE_URL || API_BACKENDS.prod;
};

/**
 * Hook to fetch replay data from the API
 * @param {string} shortCode - The replay short code
 * @returns {Object} - { data, loading, error }
 */
export const useReplayData = (shortCode) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shortCode) {
      setLoading(false);
      setError('No short code provided');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/replay/${shortCode}`);
        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to fetch replay data');
        }

        setData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shortCode]);

  return { data, loading, error };
};
